import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import Logo from "@/assets/logo.svg?react";
import { LockIcon } from "@/components/icons";
import { toast } from "@/components/toast/toast-queue";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";

import type { EmailVerificationFormData } from "./hooks/useEmailVerificationForm";
import type { PasswordCreationFormData } from "./hooks/usePasswordCreationForm";
import type { PersonalInfoFormData } from "./hooks/usePersonalInfoForm";

import { AuthContainer } from "../components/AuthContainer";
import { kickstarterApi } from "./api/kickstarter.api";
import { EmailVerificationStep } from "./components/EmailVerificationStep";
import { OnboardingProgressBar } from "./components/OnboardingProgressBar";
import { PasswordCreationStep } from "./components/PasswordCreationStep";
import { PersonalInfoStep } from "./components/PersonalInfoStep";
import { useKickstarterSignup } from "./hooks/useKickstarterSignup";
import { useOnboardingFlow } from "./hooks/useOnboardingFlow";
import { useVerifyEmail } from "./hooks/useVerifyEmail";

export function OnboardingForm() {
  const searchParams = new URLSearchParams(window.location.search);
  const emailFromUrl = searchParams.get("email");
  const stepFromUrl = Number.parseInt(searchParams.get("step") ?? "1", 10);
  const originFromUrl = searchParams.get("origin");
  const isDirectPurchase = originFromUrl === "direct-purchase";
  const initialStep = stepFromUrl ?? 1;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useGoogleTagManager("GTM-5X67HTS");

  const { currentStep, goToNextStep, goToPreviousStep, setStep } =
    useOnboardingFlow(initialStep);
  const [formData, setFormData] = useState({
    companyName: "",
    confirmPassword: "",
    creativeSupportOther: "",
    creativeSupportType: "",
    email: emailFromUrl || "",
    fullName: "",
    industry: "",
    industryOther: "",
    password: "",
    verificationCode: "",
  });
  const [applicationId, setApplicationId] = useState<null | number>(null);
  const [userId, setUserId] = useState<null | number>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const signupMutation = useKickstarterSignup();
  const verifyEmailMutation = useVerifyEmail();
  const [isCreatingPassword, setIsCreatingPassword] = useState(false);
  const [hasResentCode, setHasResentCode] = useState(false);

  useEffect(() => {
    const pollForEmail = async () => {
      if (
        emailFromUrl &&
        (initialStep === 2 || initialStep === 3 || initialStep === 4) &&
        !userId &&
        !applicationId &&
        !isInitializing
      ) {
        setIsInitializing(true);

        const maxAttempts = 20;
        let attempts = 0;

        const checkEmail = async (): Promise<boolean> => {
          try {
            // Kickstarter flow
            const result = await kickstarterApi.checkEmail(emailFromUrl);

            if (result.exists && result.userId) {
              setUserId(result.userId);
              setIsInitializing(false);
              setStep(4);

              return true;
            }

            if (result.exists && result.applicationId) {
              setApplicationId(result.applicationId);
              setIsInitializing(false);

              if (
                result.emailVerified &&
                result.status === "pending_approval"
              ) {
                setStep(3);

                return true;
              }

              if (
                !hasResentCode &&
                emailFromUrl &&
                result.status === "pending_verification"
              ) {
                setStep(2);
                try {
                  await kickstarterApi.resendCode({ email: emailFromUrl });
                  setHasResentCode(true);
                  toast.add(
                    {
                      description: "Please check your email",
                      title: "Verification code sent",
                      type: "success",
                    },
                    {
                      timeout: 5000,
                    },
                  );
                } catch (error) {
                  console.error("Failed to resend code:", error);
                }
              }

              return true;
            }

            return false;
          } catch (error) {
            console.error("Error checking email:", error);

            return false;
          }
        };

        const intervalId = setInterval(async () => {
          attempts++;

          const found = await checkEmail();

          if (found) {
            clearInterval(intervalId);
          } else if (attempts >= maxAttempts) {
            clearInterval(intervalId);
            setIsInitializing(false);
            toast.add(
              {
                description: "Please try again or contact support.",
                title: "Could not verify email",
                type: "error",
              },
              {
                timeout: 5000,
              },
            );
          }
        }, 3000);

        const found = await checkEmail();

        if (found) {
          clearInterval(intervalId);
        }

        return () => clearInterval(intervalId);
      }
    };

    if (!isDirectPurchase) {
      pollForEmail();
    }
  }, []);

  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handlePersonalInfoComplete = async (data: PersonalInfoFormData) => {
    updateFormData(data);

    try {
      const result = await signupMutation.mutateAsync({
        companyName: data.companyName,
        creativeSupportOther: data.creativeSupportOther || null,
        creativeSupportType: data.creativeSupportType,
        email: data.email,
        fullName: data.fullName,
        industry: data.industry,
        industryOther: data.industryOther || null,
      });

      setApplicationId(result.applicationId);
      goToNextStep();
      navigate({
        search: (prev) => ({
          ...prev,
          origin: "kickstarter",
        }),
        to: ".",
      });
    } catch (error: unknown) {
      console.error("Signup failed:", error);

      const errorMessage =
        (error as Error)?.message ?? "Please try again later";

      toast.add(
        {
          description: errorMessage,
          title: "Signup failed",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  };

  const handleEmailVerificationComplete = async (
    data: EmailVerificationFormData,
  ) => {
    updateFormData(data);

    if (!applicationId) {
      console.error("No applicationId found for email verification");

      toast.add(
        {
          description: "Please restart the signup process",
          title: "Session error",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );

      return;
    }
    try {
      const result = await verifyEmailMutation.mutateAsync({
        applicationId,
        code: data.verificationCode,
      });

      goToNextStep();

      toast.add(
        {
          description: result.message,
          title: "Email verified!",
          type: "success",
        },
        {
          timeout: 10_000,
        },
      );
    } catch (error) {
      console.error("Email verification failed:", error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Please check your code and try again";

      toast.add(
        {
          description: errorMessage,
          title: "Invalid verification code",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  };

  const handlePasswordCreationComplete = async (
    data: PasswordCreationFormData,
  ) => {
    updateFormData(data);

    if (!userId) {
      console.error("No userId found");

      toast.add(
        {
          description: "Please restart the signup process",
          title: "Session error",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );

      return;
    }

    setIsCreatingPassword(true);

    try {
      await queryClient.invalidateQueries({ queryKey: ["user"] });
    } catch (error) {
      console.error("Password creation failed:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Please try again";

      toast.add(
        {
          description: errorMessage,
          title: "Password creation failed",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    } finally {
      setIsCreatingPassword(false);
    }
  };

  const renderThankYouStep = () => (
    <div className="flex flex-col items-center gap-6 text-center py-8">
      <p className="text-gray-600">
        Thank you for applying for a Kickstarter account, please check your
        email for updates.
      </p>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: {
        return (
          <PersonalInfoStep
            initialData={{
              companyName: formData.companyName,
              creativeSupportOther: formData.creativeSupportOther,
              creativeSupportType: formData.creativeSupportType,
              email: formData.email,
              fullName: formData.fullName,
              industry: formData.industry,
              industryOther: formData.industryOther,
            }}
            isLoading={signupMutation.isPending}
            onComplete={handlePersonalInfoComplete}
          />
        );
      }
      case 2: {
        return (
          <EmailVerificationStep
            email={formData.email}
            initialData={{ verificationCode: formData.verificationCode }}
            isLoading={verifyEmailMutation.isPending}
            onComplete={handleEmailVerificationComplete}
            onGoBack={goToPreviousStep}
          />
        );
      }
      case 3: {
        return renderThankYouStep();
      }
      case 4: {
        return (
          <PasswordCreationStep
            initialData={{
              confirmPassword: formData.confirmPassword,
              password: formData.password,
            }}
            isLoading={isCreatingPassword}
            onComplete={handlePasswordCreationComplete}
            onGoBack={goToPreviousStep}
            userId={userId}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: {
        return "Create your free Kickstarter account";
      }
      case 2: {
        return "Verify your email";
      }
      case 3: {
        return "Thank you!";
      }
      case 4: {
        return "Create password";
      }
      default: {
        return "Account setup";
      }
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1: {
        return "";
      }
      case 2: {
        return `We've sent a code to ${formData.email}`;
      }
      case 3: {
        return "";
      }
      case 4: {
        return "Create a strong and secure password to keep your account safe";
      }
      default: {
        return "";
      }
    }
  };

  const getStepCustomIcon = () => {
    switch (currentStep) {
      case 1: {
        return null;
      }
      case 2: {
        return (
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-[#EBE9FE] rounded-full flex items-center justify-center">
              <svg
                fill="none"
                height="24"
                viewBox="0 0 32 24"
                width="32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 6L13.5 14C14.7 14.8 15.3 15.2 16 15.2C16.7 15.2 17.3 14.8 18.5 14L30 6M6 22H26C27.9 22 28.8 22 29.4 21.6C30 21.2 30.4 20.6 30.6 19.8C31 19.2 31 18.3 31 16.4V7.6C31 5.7 31 4.8 30.6 4.2C30.4 3.4 30 2.8 29.4 2.4C28.8 2 27.9 2 26 2H6C4.1 2 3.2 2 2.6 2.4C2 2.8 1.6 3.4 1.4 4.2C1 4.8 1 5.7 1 7.6V16.4C1 18.3 1 19.2 1.4 19.8C1.6 20.6 2 21.2 2.6 21.6C3.2 22 4.1 22 6 22Z"
                  stroke="#6938EF"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>
        );
      }
      case 3: {
        return null;
      }
      case 4: {
        return (
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-[#EBE9FE] rounded-full flex items-center justify-center">
              <LockIcon
                aria-label="Password creation"
                className="w-8 h-8 text-[#6938EF]"
              />
            </div>
          </div>
        );
      }
    }
  };

  if (isDirectPurchase) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-6">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <Logo className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-2xl font-semibold text-text-primary mb-3">
            Success! Check your email.
          </h2>
          <p className="text-text-secondary text-base leading-relaxed mb-6">
            Go to your inbox for the next steps.
          </p>
          <div className="flex items-center justify-center gap-2">
            <div
              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-red-500">
      <div className="flex flex-col items-center absolute top-0 left-0 right-0 z-10">
        {currentStep === 2 && (
          <OnboardingProgressBar currentStep={1} totalSteps={2} />
        )}
        {currentStep === 3 && (
          <OnboardingProgressBar currentStep={2} totalSteps={2} />
        )}

        {currentStep === 2 && (
          <h2 className="mt-8 font-regular text-text-tertiary text-center text-[20px] sm:text-[24px]">
            Welcome to NoLimit Creatives! <br /> Get started in 2 easy steps:
          </h2>
        )}
      </div>

      <div className="">
        <AuthContainer
          className={
            currentStep === 1
              ? "w-full md:w-[464px] py-[24px] px-[48px] mt-8 mb-8"
              : ""
          }
          containerClassName="!min-h-screen !h-auto"
          customIcon={getStepCustomIcon()}
          description={getStepDescription()}
          title={getStepTitle()}
        >
          {renderCurrentStep()}
        </AuthContainer>
      </div>
    </div>
  );
}
